import { sendMessage } from '../utility/message-bus';
import { Maybe } from 'maybe-not';
import { fromEvent } from 'rxjs';
import { filter, timeout, debounceTime } from 'rxjs/operators';
import { FromEventTarget } from 'rxjs/internal/observable/fromEvent';

const v1MessagePrefix = '[iFrameSizer]';
let iframeId: string;

const v1Subscription = fromEvent(window as FromEventTarget<MessageEvent>, 'message')
    .pipe(
        filter(
            e =>
                typeof e.data === 'string' &&
                e.data.includes(v1MessagePrefix) &&
                e.data.includes('iFrameResizer')
        ),
        timeout(10000)
    )
    .subscribe(
        e => {
            iframeId = e.data.substr(v1MessagePrefix.length).split(':')[0];
            window.parent.postMessage(`${v1MessagePrefix}${iframeId}:0:0:init`, '*');

            v1Subscription.unsubscribe();
        },
        () => {
            v1Subscription.unsubscribe();
        }
    );

export default function resizeIframe(
    moduleId: string,
    platformCapabilities: Set<string>,
    teardowns: (() => void)[]
) {
    let resize: () => void;

    if (platformCapabilities.has('resizeIframe')) {
        resize = () => {
            /*
                Browsers bind scrollHeight differently.
                In most documentElement.scrollHeight is a maximum height of window height and content scroll height.
                This is true for body.scrollHeight in Safari.
                Using a min allows us to observe absolutely positioned element height and shrink when necessary.
            */
            const height = Math.min(
                document.body.scrollHeight,
                document.documentElement.scrollHeight
            );
            sendMessage('resizeIframe', Maybe.just(JSON.stringify({ moduleId, height })));
        };
    } else {
        // Allowing current SDK implementations to work with removed iframeResizer library.
        resize = () => {
            if (iframeId) {
                const height = Math.min(
                    document.body.scrollHeight,
                    document.documentElement.scrollHeight
                );
                const width = document.documentElement.offsetWidth;
                window.parent.postMessage(
                    `${v1MessagePrefix}${iframeId}:${height}:${width}:mutationObserver`,
                    '*'
                );
            }
        };
    }

    const observer = new MutationObserver(resize);
    observer.observe(document.body, {
        attributes: true,
        childList: true,
        subtree: true,
        characterData: true
    });

    const resizeSubscription = fromEvent(window as FromEventTarget<Event>, 'resize')
        .pipe(debounceTime(30))
        .subscribe(resize);

    teardowns.push(() => {
        observer.disconnect();
        resizeSubscription.unsubscribe();
    });

    return resize;
}
